/**
 * ACT2-1276: one permission decides every warehouse action in Settings › Warehouses: the user's
 * RudderStack permission to create warehouse accounts in the linked workspace. The Lookout role does
 * not. Only `allowed` opens an action; every other answer blocks it with its reason.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { type QueryClient, useQuery } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';

/** The server sends the same text with an `unknown` status; this copy covers a failed request. */
export const PERMISSION_UNKNOWN_REASON = 'We could not check your permission. Try again.';
export const PERMISSION_LOADING_REASON = 'Checking your RudderStack permission.';

type PermissionAnswer =
  | Awaited<ReturnType<RouterClient<AppRouter>['settings']['warehouses']['accountCreatePermission']>>
  | undefined;

/** Why the user cannot act on warehouses, or null when RudderStack allows it. */
export function warehouseActionBlocked(answer: PermissionAnswer, request: { isError: boolean }): string | null {
  // A failed refresh does not keep a stale "allowed" enabling the actions.
  if (answer?.status === 'allowed' && !request.isError) return null;
  if (answer === undefined) return request.isError ? PERMISSION_UNKNOWN_REASON : PERMISSION_LOADING_REASON;
  return 'message' in answer ? answer.message : PERMISSION_UNKNOWN_REASON;
}

export interface WarehousePermission {
  /** Why every warehouse action is blocked, or null. */
  blocked: string | null;
  /** The first answer has not arrived: show no reason yet. */
  loading: boolean;
  /** RudderStack could not be asked: offer `retry`. */
  unknown: boolean;
  /** The RudderStack sign-in is gone or expired: offer a fresh sign-in. */
  reauthRequired: boolean;
  retry: () => void;
}

/** The permission, read through the page's shared query so every caller agrees. */
export function useWarehousePermission(): WarehousePermission {
  const orpc = useOrpc();
  const query = useQuery(orpc.settings.warehouses.accountCreatePermission.queryOptions());
  return {
    blocked: warehouseActionBlocked(query.data, query),
    loading: query.data === undefined && !query.isError,
    unknown: query.isError || query.data?.status === 'unknown',
    reauthRequired: query.data?.status === 'reauth_required',
    retry: () => void query.refetch(),
  };
}

/** True for a refusal that may mean the RudderStack permission changed. */
export function isPermissionRefusal(err: unknown): boolean {
  const code = rudderstackCode(err);
  return code !== null && !NOT_PERMISSION_CODES.has(code);
}

/** An outage is not a permission answer, so it gets its own copy. */
const NOT_PERMISSION_CODES: ReadonlySet<string> = new Set(['rudderstack_unavailable']);

function rudderstackCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const { message, data } = err as { message?: unknown; data?: { reason?: unknown } };
  if (typeof data?.reason === 'string' && data.reason.startsWith('rudderstack_')) return data.reason;
  if (typeof message === 'string' && message.startsWith('rudderstack_')) return message;
  return null;
}

/** A refused action can mean RudderStack's answer changed: read the permission again. */
export function refreshPermissionOnRefusal(
  queryClient: QueryClient,
  orpc: ReturnType<typeof useOrpc>,
  err: unknown,
): void {
  if (
    isPermissionRefusal(err) ||
    (err as { data?: { reason?: unknown } } | null)?.data?.reason === 'workspace_not_linked' ||
    (typeof err === 'object' && err !== null && 'code' in err && err.code === 'FORBIDDEN')
  ) {
    void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.accountCreatePermission.key() });
  }
}
